import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { formatPrice, getPerWordRate } from '../../config/priceConfig';

const PricePlan = () => {
  const navigate = useNavigate();

  const handlePlanSelect = (plan) => {
    if (plan.comingSoon) {
      // For coming soon plans, you could show a notification or alert
      alert('This plan will be available soon! Stay tuned.');
      return;
    }

    // Navigate to home page for the Basic plan
    navigate('/');
  };

  const plans = [
    {
      name: 'Document Translation',
      price: formatPrice(getPerWordRate('MACHINE')),
      period: '/word',
      description: 'Pay-as-you-go based on your document\'s word count.',
      features: [
        'AI-powered legal translation',
        'Supports PDF, DOCX, DOC, JPG, PNG files',
        'Standard accuracy',
        'Email support',
        'No subscription required',
      ],
      buttonText: 'Get Started',
    },
  ];

  return (
    <div className="h-screen flex flex-col bg-[#fafafd]">
      <Header />
      <main className="flex-1 overflow-y-auto">
        <div className="py-6 md:py-8 lg:py-10">
          <div className="container mx-auto px-3 sm:px-4 lg:px-6 max-w-6xl">
            <div className="text-center mb-8 md:mb-10">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                Simple, Transparent Pricing
              </h1>
              <p className="text-sm sm:text-base text-gray-600 max-w-xl mx-auto">
                Pay only for what you use. No subscriptions, no hidden fees.
              </p>
            </div>

            <div className="flex justify-center gap-4 md:gap-6">
              {plans.map((plan, idx) => (
                <div
                  key={idx}
                  className="w-full max-w-[280px] flex flex-col rounded-xl shadow-md bg-white border border-gray-200 transition hover:shadow-lg hover:-translate-y-1 duration-200 px-4 py-6"
                >
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-bold text-[#e0117e] mb-1">
                      {plan.name}
                    </h3>
                    <p className="text-xs text-gray-600 mb-3">
                      {plan.description}
                    </p>
                    <div className="flex items-baseline justify-center">
                      <span className="text-2xl md:text-3xl font-extrabold text-gray-900">
                        {plan.price}
                      </span>
                      {plan.period && (
                        <span className="text-sm text-gray-500 font-medium ml-1">
                          {plan.period}
                        </span>
                      )}
                    </div>
                  </div>

                  <button
                    className={`w-full rounded-full ${plan.comingSoon ? 'bg-gray-400' : 'bg-[#e0117e]'} text-white font-semibold text-sm py-2 mb-4 ${plan.comingSoon ? 'cursor-not-allowed' : 'hover:bg-[#b8005a]'} transition-colors shadow-sm`}
                    disabled={plan.comingSoon}
                    onClick={() => handlePlanSelect(plan)}
                  >
                    {plan.buttonText}
                  </button>

                  <ul className="space-y-2.5 flex-grow">
                    {plan.features.map((feature, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-gray-700"
                      >
                        <svg
                          className="w-4 h-4 mt-0.5 text-[#e0117e] flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="text-xs sm:text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* FAQ Section */}
            <div className="mt-12 md:mt-16 lg:mt-20">
              <h2 className="text-xl md:text-2xl font-bold text-center text-gray-900 mb-6 md:mb-8">
                Frequently Asked Questions
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
                  <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">
                    How is pricing calculated?
                  </h3>
                  <p className="text-xs md:text-sm text-gray-600">
                    Pricing is based on the word count of your source document at a rate of {formatPrice(getPerWordRate('MACHINE'))} per word. You will see an estimated cost before you proceed with the translation.
                  </p>
                </div>
                <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
                  <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">
                    What file types are supported?
                  </h3>
                  <p className="text-xs md:text-sm text-gray-600">
                    We support PDF, DOCX, DOC, and image files (JPG, PNG).
                    Additional formats will be available in upcoming releases.
                  </p>
                </div>
                <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
                  <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">
                    How accurate are the translations?
                  </h3>
                  <p className="text-xs md:text-sm text-gray-600">
                    Based on our testing, our AI displays much better legal terminology specificity relative all other AI translation services. However, this does not guarantee 100% accuracy. You are solely responsible for reviewing and ensuring the final accuracy of the translations.
                  </p>
                </div>
                <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
                  <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">
                    What payment methods are accepted?
                  </h3>
                  <p className="text-xs md:text-sm text-gray-600">
                    We accept all major credit and debit cards. All payments are processed securely.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PricePlan;
